import { action, computed } from "@ember/object";
import { trustHTML } from "@ember/template";
import { classNameBindings, classNames } from "@ember-decorators/component";
import DiscourseURL, {
  getCategoryAndTagUrl,
  getEditCategoryUrl,
} from "discourse/lib/url";
import Category from "discourse/models/category";
import CategoryDropMoreCollection from "discourse/select-kit/components/category-drop-more-collection";
import CategoryRow from "discourse/select-kit/components/category-row";
import ComboBoxComponent from "discourse/select-kit/components/combo-box";
import {
  FILTER_VISIBILITY_THRESHOLD,
  MAIN_COLLECTION,
  pluginApiIdentifiers,
  selectKitOptions,
} from "discourse/select-kit/components/select-kit";
import { categoryBadgeHTML } from "discourse/ui-kit/helpers/d-category-link";
import { i18n } from "discourse-i18n";
import CategoryDropHeader from "./category-drop/category-drop-header";

export const NO_CATEGORIES_ID = "no-categories";
export const ALL_CATEGORIES_ID = "all-categories";

const MORE_COLLECTION = "MORE_COLLECTION";

@classNames("category-drop")
@classNameBindings("noSubcategories:has-selection")
@selectKitOptions({
  none: "category.all",
  caretDownIcon: "angle-right",
  caretUpIcon: "angle-down",
  fullWidthOnMobile: true,
  noSubcategories: false,
  subCategory: false,
  clearable: false,
  hideParentCategory: "hideParentCategory",
  countSubcategories: false,
  autoInsertNoneItem: false,
  displayCategoryDescription: "displayCategoryDescription",
  headerComponent: CategoryDropHeader,
  parentCategory: false,
  allowUncategorized: "allowUncategorized",
  autoFilterable: "autoFilterable",
})
@pluginApiIdentifiers(["category-drop"])
export default class CategoryDrop extends ComboBoxComponent {
  noCategoriesLabel = i18n("categories.no_subcategories");
  navigateToEdit = false;
  editingCategory = false;
  editingCategoryTab = null;

  init() {
    super.init(...arguments);
    this.insertAfterCollection(MAIN_COLLECTION, MORE_COLLECTION);
  }

  @computed("category.id")
  get value() {
    return this.category?.id;
  }

  @computed("categoriesWithShortcuts")
  get content() {
    return this.categoriesWithShortcuts;
  }

  @computed("selectKit.options.parentCategory.displayName")
  get parentCategoryName() {
    return this.selectKit?.options?.parentCategory?.displayName;
  }

  @computed("siteSettings.allow_uncategorized_topics")
  get allowUncategorized() {
    return this.siteSettings.allow_uncategorized_topics;
  }

  modifyComponentForCollection(collection) {
    if (collection === MORE_COLLECTION) {
      return CategoryDropMoreCollection;
    }
  }

  modifyComponentForRow() {
    return CategoryRow;
  }

  @computed("selectKit.options.noSubcategories")
  get noSubcategories() {
    return this.selectKit.options.noSubcategories;
  }

  @computed
  get displayCategoryDescription() {
    return !(
      this.get("currentUser.staff") || this.get("currentUser.trust_level") > 0
    );
  }

  @computed
  get hideParentCategory() {
    return this.options.subCategory || false;
  }

  @computed("content.length", "site.lazy_load_categories")
  get autoFilterable() {
    return (
      this.site.lazy_load_categories ||
      this.content.length >= FILTER_VISIBILITY_THRESHOLD
    );
  }

  @computed("value", "selectKit.options.{subCategory,noSubcategories}")
  get shortcuts() {
    const shortcuts = [];

    if (
      (this.value && !this.editingCategory) ||
      (this.selectKit.options.noSubcategories &&
        this.selectKit.options.subCategory)
    ) {
      shortcuts.push({
        id: ALL_CATEGORIES_ID,
        name: this.allCategoriesLabel,
      });
    }

    if (
      this.selectKit.options.subCategory &&
      (this.value || !this.selectKit.options.noSubcategories)
    ) {
      shortcuts.push({
        id: NO_CATEGORIES_ID,
        name: this.noCategoriesLabel,
      });
    }

    // If there is a single shortcut, we can have a single "remove filter" option
    if (shortcuts.length === 1 && shortcuts[0].id === ALL_CATEGORIES_ID) {
      shortcuts[0].name = i18n("categories.remove_filter");
    }

    return shortcuts;
  }

  @computed("categories.[]", "shortcuts")
  get categoriesWithShortcuts() {
    const results = this._filterUncategorized(this.categories || []);
    return this.shortcuts.concat(results);
  }

  modifyNoSelection() {
    if (this.selectKit.options.noSubcategories) {
      return this.defaultItem(
        NO_CATEGORIES_ID,
        i18n("categories.no_subcategories")
      );
    } else {
      return this.defaultItem(
        ALL_CATEGORIES_ID,
        this.selectKit.options.subCategory
          ? i18n("categories.subcategories_label")
          : i18n("categories.categories_label")
      );
    }
  }

  modifySelection(content) {
    if (this.value) {
      const category = Category.findById(this.value);
      content.title = category.title;
      content.label = trustHTML(
        categoryBadgeHTML(category, {
          link: false,
          allowUncategorized: true,
          hideParent: true,
        })
      );
    }

    return content;
  }

  @computed("parentCategoryName", "selectKit.options.subCategory")
  get allCategoriesLabel() {
    if (this.editingCategory) {
      return this.noCategoriesLabel;
    }

    if (this.selectKit.options.subCategory) {
      return i18n("categories.remove_filter", {
        categoryName: this.parentCategoryName,
      });
    }

    return i18n("categories.all");
  }

  async search(filter) {
    if (this.site.lazy_load_categories) {
      let parentCategoryId;
      if (this.options.parentCategory?.id) {
        parentCategoryId = this.options.parentCategory.id;
      } else if (!filter) {
        // Only top-level categories should be displayed by default.
        // If there is a search term, the term can match any category,
        // including subcategories.
        parentCategoryId = -1;
      }

      const result = await Category.asyncSearch(filter, {
        parentCategoryId,
        includeUncategorized: this.siteSettings.allow_uncategorized_topics,
        includeAncestors: true,
        // Show all categories if possible (up to 18), otherwise show just
        // first 15 and let CategoryDropMoreCollection show the "show more" link
        limit: 18,
      });

      const categories =
        result.categoriesCount > 18
          ? result.categories.slice(0, 15)
          : result.categories;

      this.selectKit.totalCount = result.categoriesCount;

      return this.shortcuts.concat(categories);
    }

    const opts = {
      parentCategoryId: this.options.parentCategory?.id,
    };

    if (filter) {
      let results = Category.search(filter, opts);
      return this._filterUncategorized(results).sort((a, b) => {
        if (a.parent_category_id && !b.parent_category_id) {
          return 1;
        } else if (!a.parent_category_id && b.parent_category_id) {
          return -1;
        } else {
          return 0;
        }
      });
    } else {
      return this._filterUncategorized(this.content);
    }
  }

  @action
  onChange(categoryId) {
    const category =
      categoryId === ALL_CATEGORIES_ID || categoryId === NO_CATEGORIES_ID
        ? this.selectKit.options.parentCategory
        : Category.findById(parseInt(categoryId, 10));

    let route;
    if (this.editingCategoryTab) {
      // rendered on category page
      route = getEditCategoryUrl(
        category,
        categoryId !== NO_CATEGORIES_ID,
        this.editingCategoryTab
      );
    } else {
      route = getCategoryAndTagUrl(
        category,
        categoryId !== NO_CATEGORIES_ID,
        this.tag
      );
    }

    DiscourseURL.routeToUrl(route);
  }

  _filterUncategorized(content) {
    if (!this.siteSettings.allow_uncategorized_topics) {
      content = content.filter(
        (c) => c.id !== this.site.uncategorized_category_id
      );
    }

    return content;
  }
}
